package com.writerslab

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class AiReport(
    val genre: String = "",
    val mood: String = "",
    val themes: List<String> = emptyList(),
    @SerialName("themes_detail") val themesDetail: String = "",
    val setting: String = "",
    val synopsis: String = "",
    @SerialName("author_ideas") val authorIdeas: String = "",
    val score: AiScore = AiScore(),
    val review: String = "",
    val strengths: AiStrengths = AiStrengths(),
    val language: AiCategoryAssessment = AiCategoryAssessment(),
    val plot: AiCategoryAssessment = AiCategoryAssessment(),
    @SerialName("characters_assessment") val charactersAssessment: AiCategoryAssessment = AiCategoryAssessment(),
    val characters: List<AiCharacter> = emptyList(),
    val relationships: List<AiRelationship> = emptyList(),
    @SerialName("beta_reader") val betaReader: AiBetaReader = AiBetaReader(),
    val recommendations: List<AiRecommendation> = emptyList(),
    val metrics: AiMetrics = AiMetrics(),
    @SerialName("text_stats") val textStats: AiTextStats? = null,
    @SerialName("unresolved_plots") val unresolvedPlots: List<AiUnresolved> = emptyList(),
    /** Made from an earlier text than the one in the reader now (the book
     *  was edited after the run). Added by the backend. */
    @SerialName("text_changed") val textChanged: Boolean = false,
    @SerialName("text_edited_at") val textEditedAt: String? = null,
)

@Serializable
data class AiScore(
    val final: Double? = null,
    val components: Map<String, Double> = emptyMap(),
)

/**
 * Положительный фидбек от advocate-pass. Что в книге работает —
 * технически (craft: структура, темп, язык), эмоционально, что
 * запоминается, и про мир/атмосферу. Появляется как блок «Сильные
 * стороны» в Review-табе (раньше жил в удалённом Craft-табе).
 */
@Serializable
data class AiStrengths(
    val craft: List<String> = emptyList(),
    @SerialName("emotional_impact") val emotionalImpact: List<String> = emptyList(),
    val memorable: List<String> = emptyList(),
    @SerialName("world_and_atmosphere") val worldAndAtmosphere: String = "",
)

/**
 * Универсальный блок оценки категории: список yes/no/n_a-ответов на
 * атомарные вопросы + числовой score (0–10) = доля «да» от применимых.
 *
 * Используется для plot, characters, language. Frontend
 * локализует question.id через Strings.scoringQuestions().
 */
@Serializable
data class AiCategoryAssessment(
    val numeric: Double? = null,
    @SerialName("yes_count") val yesCount: Int = 0,
    @SerialName("no_count") val noCount: Int = 0,
    @SerialName("na_count") val naCount: Int = 0,
    val applicable: Int = 0,
    val questions: List<AiQuestionAnswer> = emptyList(),
)

@Serializable
data class AiQuestionAnswer(
    val id: String = "",
    val answer: String = "",
    val evidence: String = "",
)

@Serializable
data class AiCharacter(
    val name: String = "",
    val role: String = "",
    val description: String = "",
    val arc: String = "",
)

@Serializable
data class AiRelationship(
    val characters: List<String> = emptyList(),
    val type: String = "",
    val description: String = "",
)

/**
 * Прозовый фидбек бета-ридера: overall / strengths / weaknesses.
 * В скоринг не входит, отображается на Review-табе вместе с
 * advocate-strengths.
 */
@Serializable
data class AiBetaReader(
    val strengths: List<String> = emptyList(),
    val weaknesses: List<String> = emptyList(),
    val overall: String = "",
)

@Serializable
data class AiRecommendation(
    val issue: String = "",
    val where: String = "",
    val suggestion: String = "",
)

@Serializable
data class AiMetrics(
    val pacing: AiPacing = AiPacing(),
    val structure: AiStructure = AiStructure(),
    @SerialName("character_agency") val characterAgency: List<AiAgency> = emptyList(),
)

@Serializable
data class AiPacing(
    @SerialName("avg_events") val avgEvents: Double = 0.0,
    val pattern: String = "",
    @SerialName("sag_chunks") val sagChunks: List<String> = emptyList(),
)

@Serializable
data class AiStructure(
    @SerialName("has_inciting_event") val hasIncitingEvent: Boolean = false,
    @SerialName("inciting_location") val incitingLocation: String? = null,
    @SerialName("ending_resolves") val endingResolves: Boolean = false,
)

@Serializable
data class AiAgency(
    val name: String = "",
    val ratio: Double = 0.0,
)

@Serializable
data class AiUnresolved(
    val element: String = "",
    val issue: String = "",
)

@Serializable
data class AiTextStats(
    val quality: Double? = null,
    val vocabulary: Double? = null,
    val pace: Double? = null,
    val dialogue: Double? = null,
    val expressiveness: Double? = null,
    val coherence: Double? = null,
    val vividness: Double? = null,
    @SerialName("arc_volatility") val arcVolatility: Double? = null,
    val pov: String = "",
    @SerialName("mood_label") val moodLabel: String = "",
    val emotions: String = "",
)
